import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../authentication/AuthContext';
import Header from '../components/Header';
import { useDeleteNote, useNoteById, useReviewNote, useUpdateNote } from './hooks/useNote';
import { useRecallCardsForNote, useSaveRecallCard } from '../recall/hooks/useRecall';
import { useFolderById } from '../folder/hooks/useFolder';
import { useProject } from '../project/hooks/useProjects';
import NoteEditor from './NoteViewer';
import { EMPTY_EDITOR_STATE } from './noteEditorDefaults';
import AddRecallCardModal from '../recall/components/AddRecallCardModal';
import {useGenerateNoteQuiz} from '../practice/hooks/usePractice';
import {
    ArrowLeft,
    Clock,
    FolderOpen,
    Pencil,
    Trash2,
    FileText,
    Save,
    X,
    Plus,
    CheckCircle2,
    Sparkles,
} from 'lucide-react';
import Prism from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import "prismjs/components/prism-java";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-go";
import "prismjs/components/prism-kotlin";
import "prismjs/themes/prism-tomorrow.css";

const FENCED_CODE_BLOCK_REGEX = /```([a-zA-Z0-9_-]+)?[ \t]*\n([\s\S]*?)```/g;
const SUPPORTED_FENCE_LANGUAGES = new Set(['go', 'kotlin', 'javascript', 'python', 'java', 'sql']);

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function renderLexicalNode(node, index) {
    if (!node) return null;

    switch (node.type) {
        case 'root':
            return (
                <div key={index} className="flex flex-col gap-3">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </div>
            );
        case 'paragraph':
            return (
                <p key={index} className={`text-gray-200 text-base leading-relaxed ${getAlignment(node.format)}`}>
                    {node.children?.length ? node.children.map((child, i) => renderLexicalNode(child, i)) : <br />}
                </p>
            );
        case 'heading': {
            const sizeMap = { h1: 'text-2xl', h2: 'text-xl', h3: 'text-lg', h4: 'text-base', h5: 'text-sm', h6: 'text-xs' };
            const Tag = node.tag ?? 'h2';
            return (
                <Tag key={index} className={`font-bold text-gray-100 ${sizeMap[Tag] ?? 'text-xl'} ${getAlignment(node.format)}`}>
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </Tag>
            );
        }
        case 'text': {
            const content = node.text;
            if (!content) return null;
            const fmt = node.format ?? 0;
            const isBold = fmt & 1;
            const isItalic = fmt & 2;
            const isUnder = fmt & 8;
            const isStrike = fmt & 4;
            const isCode = fmt & 16;
            if (isCode) return <code key={index} className="bg-gray-800 text-indigo-300 rounded px-1 py-0.5 text-sm font-mono">{content}</code>;
            let el = <span key={index}>{content}</span>;
            if (isBold) el = <strong key={index} className="font-semibold text-gray-100">{content}</strong>;
            if (isItalic) el = <em key={index} className="italic">{content}</em>;
            if (isUnder) el = <u key={index}>{content}</u>;
            if (isStrike) el = <s key={index} className="line-through text-gray-500">{content}</s>;
            return el;
        }
        case 'link':
            return (
                <a key={index} href={node.url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 underline hover:text-indigo-300 transition">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </a>
            );
        case 'list':
            return node.listType === 'bullet'
                ? <ul key={index} className="list-disc list-inside flex flex-col gap-1 text-gray-200">{node.children?.map((child, i) => renderLexicalNode(child, i))}</ul>
                : <ol key={index} className="list-decimal list-inside flex flex-col gap-1 text-gray-200">{node.children?.map((child, i) => renderLexicalNode(child, i))}</ol>;
        case 'listitem':
            return (
                <li key={index} className="text-gray-200 leading-relaxed">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </li>
            );
        case 'quote':
            return (
                <blockquote key={index} className="border-l-4 border-indigo-500/50 pl-4 text-gray-400 italic my-1">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </blockquote>
            );
        case 'code':
            return (
                <pre key={index} className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-sm text-indigo-300 font-mono overflow-x-auto my-1">
                    {node.children?.map((child, i) => renderLexicalNode(child, i))}
                </pre>
            );
        case 'linebreak':
            return <br key={index} />;
        default:
            return node.children ? <span key={index}>{node.children.map((child, i) => renderLexicalNode(child, i))}</span> : null;
    }
}

function getAlignment(format) {
    switch (format) {
        case 'center': return 'text-center';
        case 'right': return 'text-right';
        case 'justify': return 'text-justify';
        default: return '';
    }
}

function normalizeFenceLanguage(language) {
    const normalized = String(language ?? '').trim().toLowerCase();

    switch (normalized) {
        case 'js':
            return 'javascript';
        case 'py':
            return 'python';
        case 'golang':
            return 'go';
        case 'kt':
            return 'kotlin';
        default:
            return normalized || 'plain';
    }
}

function extractTextWithBreaks(node) {
    if (!node) return '';
    if (node.type === 'linebreak') return '\n';
    if (node.type === 'text') return node.text ?? '';

    const childrenText = (node.children ?? []).map(extractTextWithBreaks).join('');
    if (['paragraph', 'heading', 'listitem', 'quote', 'code'].includes(node.type)) {
        return `${childrenText}\n`;
    }

    return childrenText;
}

function containsFencedCodeBlocks(value) {
    if (!value) return false;
    FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
    return FENCED_CODE_BLOCK_REGEX.test(value);
}

function renderFencedCodeContent(rawText) {
    if (!rawText) return null;

    const blocks = [];
    let cursor = 0;
    FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
    let match = FENCED_CODE_BLOCK_REGEX.exec(rawText);

    while (match) {
        const [fullMatch, language, code] = match;
        const matchStart = match.index;
        const before = rawText.slice(cursor, matchStart);

        if (before.trim()) {
            blocks.push(
                <p key={`text-${matchStart}`} className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap">
                    {before.trim()}
                </p>
            );
        }

        const normalizedLanguage = normalizeFenceLanguage(language);
        const activeLanguage = SUPPORTED_FENCE_LANGUAGES.has(normalizedLanguage) ? normalizedLanguage : 'plain';
        const highlightedCode = activeLanguage === 'plain'
            ? Prism.util.encode(code)
            : Prism.highlight(code, Prism.languages[activeLanguage] ?? Prism.languages.javascript, activeLanguage);

        blocks.push(
            <pre key={`code-${matchStart}`} className="bg-gray-900 border border-gray-700 rounded-lg p-4 text-sm text-indigo-300 font-mono overflow-x-auto my-1">
                <code
                    className={`language-${activeLanguage}`}
                    dangerouslySetInnerHTML={{__html: highlightedCode}}
                />
            </pre>
        );

        cursor = matchStart + fullMatch.length;
        match = FENCED_CODE_BLOCK_REGEX.exec(rawText);
    }

    const after = rawText.slice(cursor);
    if (after.trim()) {
        blocks.push(
            <p key="text-tail" className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap">
                {after.trim()}
            </p>
        );
    }

    if (blocks.length === 0) {
        return <p className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap">{rawText}</p>;
    }

    return <div className="flex flex-col gap-3">{blocks}</div>;
}

function renderNoteContent(raw) {
    if (!raw) return null;
    try {
        const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const root = json?.root ?? json;
        const plainText = extractTextWithBreaks(root).trim();
        if (containsFencedCodeBlocks(plainText)) {
            return renderFencedCodeContent(plainText);
        }
        return renderLexicalNode(root, 0);
    } catch {
        if (containsFencedCodeBlocks(raw)) {
            return renderFencedCodeContent(raw);
        }
        return <p className="text-gray-200 text-base leading-relaxed whitespace-pre-wrap">{raw}</p>;
    }
}

function extractPlainText(node) {
    if (!node) return '';
    if (node.type === 'text') return node.text ?? '';
    return (node.children ?? []).map(extractPlainText).join(' ');
}

function getWordCount(raw) {
    if (!raw) return 0;
    try {
        const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const text = extractPlainText(json?.root ?? json);
        return text.trim().split(/\s+/).filter(Boolean).length;
    } catch {
        return raw.trim().split(/\s+/).filter(Boolean).length;
    }
}

function getReadTime(raw) {
    const words = getWordCount(raw);
    const mins = Math.max(1, Math.ceil(words / 200));
    return `${mins} min read`;
}

function formatRelativeDay(dateValue) {
    if (!dateValue) return 'Never';

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Never';

    const diffDays = Math.round((date.getTime() - Date.now()) / 86400000);
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    if (diffDays > 1) return `in ${diffDays} days`;
    if (diffDays === -1) return '1 day ago';
    return `${Math.abs(diffDays)} days ago`;
}

function getDueLabel(nextRevisionDate) {
    if (!nextRevisionDate) return 'Start review';

    const date = new Date(nextRevisionDate);
    if (Number.isNaN(date.getTime())) return 'Start review';

    return date.getTime() <= Date.now()
        ? 'Due now'
        : `Due ${formatRelativeDay(nextRevisionDate)}`;
}

export const NoteDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { authToken, user } = useAuth();
    const { data: note, isLoading } = useNoteById(authToken, id);
    const { data: noteRecallCards = [], refetch: refetchNoteRecallCards } = useRecallCardsForNote(authToken, id);
    const { mutateAsync: saveRecallCard } = useSaveRecallCard(authToken);
    const [isEditing, setIsEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState('');
    const [draftTags, setDraftTags] = useState('');
    const [draftContent, setDraftContent] = useState(EMPTY_EDITOR_STATE);
    const [draftParentFolderId, setDraftParentFolderId] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showAddRecallCardModal, setShowAddRecallCardModal] = useState(false);
    const [isAddingRecallCard, setIsAddingRecallCard] = useState(false);
    const [addRecallCardError, setAddRecallCardError] = useState('');
    const [showAllRecallCards, setShowAllRecallCards] = useState(false);
    const { mutate: deleteNote } = useDeleteNote(authToken);
    const { mutateAsync: updateNote } = useUpdateNote(authToken);
    const { mutateAsync: reviewNote, isLoading: isReviewing } = useReviewNote(authToken);
    const [reviewError, setReviewError] = useState('');
    const { mutateAsync: generateNoteQuiz, isLoading: isGeneratingQuiz } = useGenerateNoteQuiz(authToken);
    const [quizError, setQuizError] = useState('');

    useEffect(() => {
        if (!note) return;
        if (!isEditing) {
            setDraftTitle(note.title ?? '');
            setDraftTags(note.tags ?? '');
            setDraftContent(note.content || EMPTY_EDITOR_STATE);
            setDraftParentFolderId(note.parentFolderId ?? '');
        }
    }, [note, isEditing]);

    useEffect(() => {
        setShowAllRecallCards(false);
    }, [id]);

    const folderIdForDisplay = isEditing ? draftParentFolderId : (note?.parentFolderId ?? '');
    const { data: folder, isLoading: isFolderLoading } = useFolderById(authToken, folderIdForDisplay);
    const projectIdForDisplay = note?.projectId ?? null;
    const { data: project, isLoading: isProjectLoading } = useProject(authToken, projectIdForDisplay);
    const folderLabel = !folderIdForDisplay
        ? 'No Folder'
        : folder?.name ?? (isFolderLoading ? 'Loading folder...' : folderIdForDisplay);
    const projectLabel = !projectIdForDisplay
        ? 'No Project'
        : project?.name ?? (isProjectLoading ? 'Loading project...' : String(projectIdForDisplay));
    const contentForStats = isEditing ? draftContent : note?.content;
    const editorKey = `${note?.id ?? 'note'}-${isEditing ? 'edit' : 'view'}`;
    const noteRecallCardIds = noteRecallCards.map((card) => card.id);
    const noteRecallCardsQuery = noteRecallCardIds.length > 0 ? `?cards=${noteRecallCardIds.join(',')}` : '';
    const isNoteReviewDue = !note?.nextReviewDate || new Date(note.nextReviewDate).getTime() <= Date.now();
    const visibleRecallCards = useMemo(
        () => (showAllRecallCards ? noteRecallCards : noteRecallCards.slice(0, 5)),
        [noteRecallCards, showAllRecallCards]
    );

    const handleDelete = () => {
        deleteNote(id, {
            onSuccess: () => navigate('/notes'),
        });
    };

    const startEditing = () => {
        if (!note) return;
        setSaveError('');
        setConfirmDelete(false);
        setDraftTitle(note.title ?? '');
        setDraftTags(note.tags ?? '');
        setDraftContent(note.content || EMPTY_EDITOR_STATE);
        setDraftParentFolderId(note.parentFolderId ?? '');
        setIsEditing(true);
    };

    const cancelEditing = () => {
        if (!note) return;
        setSaveError('');
        setIsEditing(false);
        setDraftTitle(note.title ?? '');
        setDraftTags(note.tags ?? '');
        setDraftContent(note.content || EMPTY_EDITOR_STATE);
        setDraftParentFolderId(note.parentFolderId ?? '');
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');

        try {
            await updateNote({
                id,
                noteData: {
                    tags: draftTags,
                    title: draftTitle,
                    content: draftContent,
                    parentFolderId: draftParentFolderId || '',
                    projectId: note?.projectId ?? null,
                },
            });
            setIsEditing(false);
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Note could not be updated.');
        } finally {
            setSaving(false);
        }
    };

    const handleAddRecallCard = async (data) => {
        setIsAddingRecallCard(true);
        setAddRecallCardError('');

        try {
            await saveRecallCard({
                userId: user?.uid,
                front: data.title,
                back: data.deadline,
                hint: data.repeat,
                tags: data.tags,
                parentFolderId: '',
                sourceNoteId: note?.id ?? id,
                aiGenerated: false,
            });
            await refetchNoteRecallCards();
            setShowAddRecallCardModal(false);
            setShowAllRecallCards(false);
        } catch (error) {
            setAddRecallCardError(error instanceof Error ? error.message : 'Recall card could not be created.');
        } finally {
            setIsAddingRecallCard(false);
        }
    };

    const handleReview = async () => {
        if (!isNoteReviewDue || isReviewing) return;
        setReviewError('');
        try {
            await reviewNote(id);
        } catch (error) {
            setReviewError(error instanceof Error ? error.message : 'Note could not be marked as reviewed.');
        }
    };

    const handleGenerateQuiz = async () => {
        if (isGeneratingQuiz) return;
        setQuizError('');
        try {
            const generatedQuiz = await generateNoteQuiz(id);
            navigate('/practice/preview', {
                state: {
                    generatedQuiz,
                    noteTitle: note?.title ?? '',
                },
            });
        } catch (error) {
            setQuizError(error instanceof Error ? error.message : 'AI quiz could not be generated.');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-400 text-sm animate-pulse">Loading...</div>
            </div>
        );
    }

    if (!note) {
        return (
            <div className="min-h-screen bg-[#12131a] flex items-center justify-center">
                <div className="text-gray-500 text-sm">Note not found.</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#12131a]">
            <Header />
            <div className="max-w-3xl mx-auto px-6 pt-24 pb-20">
                <button
                    onClick={() => navigate('/notes')}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition mb-8"
                >
                    <ArrowLeft size={16} />
                    Back to notes
                </button>

                <div className="flex items-start justify-between gap-4 mb-6">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="mt-1 flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                            <FileText size={17} className="text-indigo-400" />
                        </div>
                        {isEditing ? (
                            <input
                                type="text"
                                value={draftTitle}
                                onChange={(e) => setDraftTitle(e.target.value)}
                                className="w-full bg-transparent text-2xl font-bold text-gray-100 placeholder-gray-600 outline-none caret-indigo-400 leading-snug"
                                placeholder="Untitled note"
                            />
                        ) : (
                            <h1 className="text-2xl font-bold text-gray-100 leading-snug break-words">
                                {note.title || 'Untitled note'}
                            </h1>
                        )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition disabled:opacity-60"
                                >
                                    <Save size={14} />
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    onClick={cancelEditing}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-800 text-sm transition"
                                >
                                    <X size={14} />
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={startEditing}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-800 text-sm transition"
                                >
                                    <Pencil size={14} />
                                    Edit
                                </button>

                                {confirmDelete ? (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleDelete}
                                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm transition"
                                        >
                                            <Trash2 size={13} />
                                            Yes, delete
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(false)}
                                            className="px-3 py-2 rounded-lg border border-gray-700 text-gray-400 hover:bg-gray-800 text-sm transition"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDelete(true)}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-700 text-gray-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/5 text-sm transition"
                                    >
                                        <Trash2 size={14} />
                                        Delete
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-6 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">
                        <Clock size={12} />
                        {formatDate(note.updatedAt)}
                    </span>
                    {folderIdForDisplay && (
                        <span className="flex items-center gap-1.5">
                            <FolderOpen size={12} />
                            {folderLabel}
                        </span>
                    )}
                    {projectIdForDisplay && (
                        <span className="flex items-center gap-1.5">
                            <FolderOpen size={12} />
                            {projectLabel}
                        </span>
                    )}
                    <span className="flex items-center gap-1.5 text-gray-600">
                        {getWordCount(contentForStats)} words · {getReadTime(contentForStats)}
                    </span>
                </div>

                <div className="h-px bg-gray-800 mb-8" />

                {isEditing ? (
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-6 shadow-lg">
                        <div className="grid gap-4 mb-5">
                            <label className="grid gap-2">
                                <span className="text-xs uppercase tracking-widest text-gray-500">Tags</span>
                                <input
                                    type="text"
                                    value={draftTags}
                                    onChange={(e) => setDraftTags(e.target.value)}
                                    className="w-full rounded-lg border border-gray-700 bg-[#12131a] px-3 py-2 text-sm text-gray-100 outline-none focus:border-indigo-500/50"
                                    placeholder="#cs, #backend"
                                />
                            </label>

                            <label className="grid gap-2">
                                <span className="text-xs uppercase tracking-widest text-gray-500">Parent folder ID</span>
                                <input
                                    type="text"
                                    value={draftParentFolderId}
                                    onChange={(e) => setDraftParentFolderId(e.target.value)}
                                    className="w-full rounded-lg border border-gray-700 bg-[#12131a] px-3 py-2 text-sm text-gray-100 outline-none focus:border-indigo-500/50"
                                    placeholder="Optional folder id"
                                />
                            </label>
                        </div>

                        <div className="rounded-xl overflow-hidden border border-gray-800">
                            <NoteEditor
                                key={editorKey}
                                initialEditorState={draftContent || EMPTY_EDITOR_STATE}
                                onChange={setDraftContent}
                            />
                        </div>

                        <div className="mt-5 rounded-xl border border-gray-800 bg-[#171a22] p-4">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Live preview</p>
                            {renderNoteContent(draftContent)}
                        </div>

                        {saveError ? (
                            <p className="mt-4 text-sm text-red-400">{saveError}</p>
                        ) : null}
                    </div>
                ) : (
                    <div className="bg-[#1E1F25] border border-[#2A2B32] rounded-2xl p-8 shadow-lg min-h-[300px]">
                        {note.content ? (
                            renderNoteContent(note.content)
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                                <FileText size={40} className="mb-3 opacity-30" />
                                <p className="text-sm">This note is empty.</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1.5">
                        <Clock size={11} />
                        Created {formatDate(note.createdAt)}
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1.5">
                        <FolderOpen size={11} />
                        {folderLabel}
                    </span>
                    {projectIdForDisplay && (
                        <>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1.5">
                                <FolderOpen size={11} />
                                {projectLabel}
                            </span>
                        </>
                    )}
                    <span>•</span>
                    <span>Last reviewed: {formatRelativeDay(note.reviewedAt)}</span>
                    <span>•</span>
                    <span>{getDueLabel(note.nextReviewDate)}</span>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                    {(reviewError || quizError) ? (
                        <p className="mr-auto text-sm text-red-400">{reviewError || quizError}</p>
                    ) : null}
                    <button
                        type="button"
                        onClick={handleGenerateQuiz}
                        disabled={isGeneratingQuiz || !note.content}
                        className="inline-flex items-center gap-2 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm font-medium text-violet-200 transition hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <Sparkles size={15} />
                        {isGeneratingQuiz ? 'Generating...' : 'Generate AI Quiz'}
                    </button>
                    <button
                        type="button"
                        onClick={handleReview}
                        disabled={isReviewing || !isNoteReviewDue}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <CheckCircle2 size={15} />
                        {isReviewing
                            ? 'Saving...'
                            : (isNoteReviewDue ? 'Mark as reviewed' : getDueLabel(note.nextReviewDate))}
                    </button>
                </div>

                <div className="mt-8">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <h2 className="text-sm font-semibold text-gray-200">
                            Recall Cards · {noteRecallCards.length}
                        </h2>
                        <button
                            type="button"
                            onClick={() => {
                                setAddRecallCardError('');
                                setShowAddRecallCardModal(true);
                            }}
                            className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 transition"
                        >
                            <Plus size={12} />
                            Create recall card
                        </button>
                    </div>

                    <div className="flex flex-col gap-2">
                        {visibleRecallCards.map((card) => (
                            <button
                                key={card.id}
                                type="button"
                                onClick={() => navigate(`/recall/${card.id}`)}
                                className="w-full rounded-xl border border-[#2A2B32] bg-[#1E1F25] px-4 py-3 text-left transition hover:border-indigo-500/30 hover:bg-[#23252d]"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-gray-100">
                                            {card.front || 'Untitled card'}
                                        </p>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Last reviewed: {formatRelativeDay(card.lastRevisionDate)}
                                        </p>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-gray-700 px-2 py-1 text-[11px] text-gray-300">
                                        {getDueLabel(card.nextRevisionDate)}
                                    </span>
                                </div>
                            </button>
                        ))}

                        {noteRecallCards.length === 0 && (
                            <div className="rounded-xl border border-dashed border-gray-700 px-4 py-6 text-center text-sm text-gray-500">
                                No recall cards yet.
                            </div>
                        )}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                        {noteRecallCards.length > 5 ? (
                            <button
                                type="button"
                                onClick={() => setShowAllRecallCards((current) => !current)}
                                className="inline-flex items-center gap-1.5 text-sm text-indigo-400 hover:text-indigo-300 transition"
                            >
                                {showAllRecallCards ? 'Show less' : `View all ${noteRecallCards.length}`}
                            </button>
                        ) : (
                            <span />
                        )}
                        <button
                            type="button"
                            onClick={() => navigate(`/recall/review${noteRecallCardsQuery}`)}
                            disabled={noteRecallCardIds.length === 0}
                            className="text-sm text-gray-400 hover:text-gray-200 transition disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Start review
                        </button>
                    </div>
                    {addRecallCardError ? (
                        <p className="mt-3 text-sm text-red-400">{addRecallCardError}</p>
                    ) : null}
                </div>
            </div>
            {showAddRecallCardModal ? (
                <AddRecallCardModal
                    onSubmit={handleAddRecallCard}
                    onClose={() => {
                        if (isAddingRecallCard) return;
                        setAddRecallCardError('');
                        setShowAddRecallCardModal(false);
                    }}
                    initialFront={note.title ?? ''}
                    initialTags={note.tags ?? ''}
                    isSubmitting={isAddingRecallCard}
                />
            ) : null}
        </div>
    );
};
